import type { MessageKey, Vars } from '../../i18n';
import type {
  MonthRow,
  OrganizationRow,
  PlatformFigures,
  PlatformReport,
} from '../../lib/api/platformReports';

// ─────────────────────────────────────────────────────────────────────
// THE REPORT AS A TABLE, AND THE TABLE AS CSV (#1043).
//
// One model for both: the section renders `buildReportTable(...)` and the
// CSV export writes the SAME columns, rows and total — so «download what I
// see» cannot drift from what is on the screen. Every cell carries its
// on-screen text and its CSV value; numbers go to the CSV unformatted (a
// spreadsheet must be able to add them up), text goes through `csvCell`.
//
// CSV, BUILT IN THE BROWSER FROM THE RESPONSE:
//
//   • `;` between cells and `,` as the decimal mark, CRLF line ends and a
//     UTF-8 BOM — what Norwegian Excel opens directly, and the convention of
//     the club's own settlement CSV (`ledgerToCsv`).
//   • A cell is quoted when it holds `;`, `"`, a line break, or leading or
//     trailing space; a quote inside is doubled.
//   • FORMULA INJECTION: a TEXT cell that begins with `=`, `+`, `-`, `@`, a
//     tab or a carriage return is prefixed with `'`, so a spreadsheet shows it
//     rather than evaluating it. An organization's name is typed by a person
//     and is the cell this exists for. Numbers are emitted by this code from
//     numbers, never from text, and are not prefixed.
// ─────────────────────────────────────────────────────────────────────

export interface ReportFormatters {
  t: (key: MessageKey, vars?: Vars) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string;
}

export interface ReportCell {
  /** What the screen shows. */
  display: string;
  /** What the CSV holds: text (guarded and quoted) or a number (as written). */
  csv: { text: string } | { number: string };
}

export interface ReportColumn {
  id: string;
  label: string;
  numeric: boolean;
}

export interface ReportTable {
  columns: ReportColumn[];
  rows: { key: string; cells: ReportCell[] }[];
  total: ReportCell[];
}

/** Øre as kroner with two decimals, by integer arithmetic — never a float's rounding. */
export function kronerCsv(ore: number): string {
  const sign = ore < 0 ? '-' : '';
  const abs = Math.abs(ore);
  return `${sign}${Math.trunc(abs / 100)},${String(abs % 100).padStart(2, '0')}`;
}

/** Minutes as hours, two decimals at most, `,` as the mark. */
export function hoursCsv(minutes: number): string {
  const hundredths = Math.round((minutes * 100) / 60);
  return kronerCsv(hundredths)
    .replace(/,00$/, '')
    .replace(/(,\d)0$/, '$1');
}

const FORMULA_START = /^[=+\-@\t\r]/;

/** One CSV cell. Text is formula-guarded; anything that needs quoting is quoted. */
export function csvCell(cell: ReportCell['csv']): string {
  if ('number' in cell) return cell.number;
  const guarded = FORMULA_START.test(cell.text) ? `'${cell.text}` : cell.text;
  return /[;"\r\n]|^\s|\s$/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/** The whole table as CSV: header, rows, the total; BOM and CRLF. */
export function reportTableToCsv(table: ReportTable): string {
  const line = (cells: ReportCell['csv'][]) => cells.map(csvCell).join(';');
  const lines = [
    line(table.columns.map((c) => ({ text: c.label }))),
    ...table.rows.map((r) => line(r.cells.map((c) => c.csv))),
    line(table.total.map((c) => c.csv)),
  ];
  return '﻿' + lines.join('\r\n') + '\r\n';
}

/** Days in a month, from the local calendar. */
const daysIn = (year: number, month: number) => new Date(year, month, 0).getDate();

export function buildReportTable(report: PlatformReport, f: ReportFormatters): ReportTable {
  const count = (n: number): ReportCell => ({
    display: f.formatNumber(n),
    csv: { number: String(n) },
  });
  const money = (ore: number): ReportCell => ({
    display: f.formatNumber(ore / 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    csv: { number: kronerCsv(ore) },
  });
  const hours = (minutes: number): ReportCell => ({
    display: f.formatNumber(minutes / 60, { maximumFractionDigits: 2 }),
    csv: { number: hoursCsv(minutes) },
  });
  const text = (value: string): ReportCell => ({ display: value, csv: { text: value } });

  const activityCells = (r: {
    bookings: number;
    courtMinutes: number;
    activities: number;
    signups: number;
  }) => [count(r.bookings), hours(r.courtMinutes), count(r.activities), count(r.signups)];
  const moneyCells = (r: PlatformFigures | OrganizationRow) => [
    money(r.payments.grossOre),
    money(r.payments.refundedOre),
    money(r.payments.platformFeesOre),
  ];
  const activityColumns: ReportColumn[] = [
    { id: 'bookings', label: f.t('saReports.col.bookings'), numeric: true },
    { id: 'courtHours', label: f.t('saReports.col.courtHours'), numeric: true },
    { id: 'activities', label: f.t('saReports.col.activities'), numeric: true },
    { id: 'signups', label: f.t('saReports.col.signups'), numeric: true },
  ];
  const moneyColumns: ReportColumn[] = [
    { id: 'gross', label: f.t('saReports.col.gross'), numeric: true },
    { id: 'refunded', label: f.t('saReports.col.refunded'), numeric: true },
    { id: 'platformFees', label: f.t('saReports.col.platformFees'), numeric: true },
  ];

  if (report.months) {
    const monthLabel = (m: MonthRow) => {
      const [y, mo] = m.month.split('-').map(Number) as [number, number];
      const name = f.formatDate(new Date(y, mo - 1, 1), { month: 'long', year: 'numeric' });
      const partial = m.from !== `${m.month}-01` || m.to !== `${m.month}-${daysIn(y, mo)}`;
      return partial
        ? f.t('saReports.partialMonth', {
            month: name,
            from: String(Number(m.from.slice(8))),
            to: String(Number(m.to.slice(8))),
          })
        : name;
    };
    const figures = (r: PlatformFigures) => [
      count(r.organizations.CENTRE),
      count(r.activeOrganizations.CENTRE),
      count(r.organizations.CLUB),
      count(r.activeOrganizations.CLUB),
      ...activityCells(r),
      count(r.signedInAccounts),
      count(r.newAccounts),
      ...moneyCells(r),
    ];
    return {
      columns: [
        { id: 'month', label: f.t('saReports.col.month'), numeric: false },
        { id: 'centres', label: f.t('saReports.col.centres'), numeric: true },
        { id: 'activeCentres', label: f.t('saReports.col.activeCentres'), numeric: true },
        { id: 'clubs', label: f.t('saReports.col.clubs'), numeric: true },
        { id: 'activeClubs', label: f.t('saReports.col.activeClubs'), numeric: true },
        ...activityColumns,
        { id: 'signedIn', label: f.t('saReports.col.signedIn'), numeric: true },
        { id: 'newAccounts', label: f.t('saReports.col.newAccounts'), numeric: true },
        ...moneyColumns,
      ],
      rows: report.months.map((m) => ({
        key: m.month,
        cells: [{ display: monthLabel(m), csv: { text: m.month } }, ...figures(m)],
      })),
      total: [text(f.t('saReports.total')), ...figures(report.total)],
    };
  }

  const organizations = report.organizations ?? [];
  const kindLabel = (kind: string) =>
    kind === 'CENTRE'
      ? f.t('saReports.kind.CENTRE')
      : kind === 'CLUB'
        ? f.t('saReports.kind.CLUB')
        : kind;
  const activeCount = organizations.filter((o) => o.active).length;
  return {
    columns: [
      { id: 'organization', label: f.t('saReports.col.organization'), numeric: false },
      { id: 'kind', label: f.t('saReports.col.kind'), numeric: false },
      { id: 'active', label: f.t('saReports.col.active'), numeric: false },
      ...activityColumns,
      ...moneyColumns,
    ],
    rows: organizations.map((o) => ({
      key: o.organizationId,
      cells: [
        text(o.organizationName),
        text(kindLabel(o.kind)),
        text(o.active ? f.t('saReports.yes') : f.t('saReports.no')),
        ...activityCells(o),
        ...moneyCells(o),
      ],
    })),
    total: [
      text(f.t('saReports.totalOrganizations', { count: organizations.length })),
      text(''),
      text(f.t('saReports.activeCount', { count: activeCount })),
      ...activityCells(report.total),
      ...moneyCells(report.total),
    ],
  };
}
